import { api } from './api-client';
import { STORAGE_KEYS, getItem } from './storage';
import type { ChatMessage, ConvSummary } from '@/hooks/useMessenger';

interface BackendMessage {
  id: string;
  conversationId: string;
  senderId: string;
  type?: string;
  content: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  createdAt: string;
  isDeleted?: boolean;
  sender?: { id: string; name?: string; username?: string };
}

interface BackendConversation {
  id: string;
  name: string;
  type: 'DIRECT' | 'GROUP';
  avatar: string | null;
  lastMessage: BackendMessage | null;
  lastMessageAt: string | null;
  unreadCount: number;
  isPinned: boolean;
  isMuted: boolean;
}

async function currentUserId(): Promise<string | null> {
  const raw = await getItem(STORAGE_KEYS.USER);
  if (!raw) return null;
  try {
    return (JSON.parse(raw) as { id: string }).id;
  } catch {
    return null;
  }
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

const GRADIENTS = [
  'linear-gradient(135deg,#FF9500,#FFCC00)',
  'linear-gradient(135deg,#AF52DE,#FF2D92)',
  'linear-gradient(135deg,#0071E3,#5AC8FA)',
  'linear-gradient(135deg,#34C759,#00C7BE)',
  'linear-gradient(135deg,#5856D6,#0071E3)',
  'linear-gradient(135deg,#FF2D92,#FF3B30)',
];

function gradientFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return GRADIENTS[Math.abs(h) % GRADIENTS.length] ?? GRADIENTS[0]!;
}

function formatShortTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  const ms = now.getTime() - d.getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function adaptConv(c: BackendConversation): ConvSummary {
  return {
    id: c.id,
    name: c.name,
    initials: initials(c.name),
    avatarGradient: gradientFor(c.id),
    online: 'online',
    lastMessage: c.lastMessage?.content ?? '',
    lastTime: formatShortTime(c.lastMessage?.createdAt ?? c.lastMessageAt),
    unread: c.unreadCount ?? 0,
  };
}

/**
 * Upload endpoint returns URL like `/api/im/files/xxx` (missing /v1). Fix
 * to a same-origin-relative URL the WebView can actually fetch.
 */
function normalizeFileUrl(url: string): string {
  if (url.startsWith('/api/v1/')) return url;
  if (url.startsWith('/api/im/')) return url.replace('/api/im/', '/api/v1/im/');
  return url;
}

const RICH_TYPES = new Set(['IMAGE', 'FILE', 'LOCATION']);

function adaptMsg(m: BackendMessage, meId: string | null): ChatMessage {
  const senderName = m.sender?.name ?? m.sender?.username ?? '?';
  const type = (m.type?.toUpperCase() ?? 'TEXT') as ChatMessage['type'];

  const textField =
    type === 'IMAGE' || type === 'FILE'
      ? normalizeFileUrl(m.content)
      : m.content;

  const base: ChatMessage = {
    id: m.id,
    threadId: m.conversationId,
    text: textField,
    time: formatShortTime(m.createdAt),
    fromSelf: Boolean(meId) && m.senderId === meId,
    authorInitials: initials(senderName),
  };
  if (type && RICH_TYPES.has(type)) base.type = type;
  if (m.fileName) base.fileName = m.fileName;
  if (typeof m.fileSize === 'number') base.fileSize = m.fileSize;
  if (m.mimeType) base.mimeType = m.mimeType;
  return base;
}

export async function fetchConversations(): Promise<ConvSummary[]> {
  const { data } = await api.get<BackendConversation[]>('/im/conversations');
  return data.map(adaptConv);
}

export async function fetchMessages(threadId: string): Promise<ChatMessage[]> {
  const [{ data }, meId] = await Promise.all([
    api.get<BackendMessage[]>(`/im/conversations/${threadId}/messages`, { params: { limit: 50 } }),
    currentUserId(),
  ]);
  return data.filter((m) => !m.isDeleted).map((m) => adaptMsg(m, meId));
}

export async function sendMessage(threadId: string, text: string): Promise<ChatMessage> {
  const [{ data }, meId] = await Promise.all([
    api.post<BackendMessage>(`/im/conversations/${threadId}/messages`, { content: text }),
    currentUserId(),
  ]);
  return adaptMsg(data, meId);
}

export interface UploadedFileMeta {
  url: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
}

/**
 * Upload a file for the IM channel. Backend returns a wrapped payload
 * `{success, file: {...}}` — the response interceptor already unwrapped
 * the outer `{code, message, data}` envelope, so we just pull `.file`.
 */
export async function uploadFile(file: File): Promise<UploadedFileMeta> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<{ success: boolean; file: UploadedFileMeta; error?: string }>(
    '/im/upload',
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Let axios set the boundary itself — explicit overwrite of the default
      // 'application/json' header on the instance.
    },
  );
  if (!data.success || !data.file) throw new Error(data.error ?? 'Upload failed');
  return { ...data.file, url: normalizeFileUrl(data.file.url) };
}

type RichPayload =
  | { kind: 'image'; url: string; fileName: string; fileSize: number; mimeType: string }
  | { kind: 'file'; url: string; fileName: string; fileSize: number; mimeType: string }
  | { kind: 'location'; lat: number; lng: number };

export async function sendRichMessage(
  threadId: string,
  payload: RichPayload,
): Promise<ChatMessage> {
  const meId = await currentUserId();
  const body =
    payload.kind === 'image'
      ? { type: 'IMAGE', content: payload.url, fileName: payload.fileName, fileSize: payload.fileSize, mimeType: payload.mimeType }
      : payload.kind === 'file'
        ? { type: 'FILE', content: payload.url, fileName: payload.fileName, fileSize: payload.fileSize, mimeType: payload.mimeType }
        : { type: 'LOCATION', content: `${payload.lat.toFixed(6)},${payload.lng.toFixed(6)}` };
  const { data } = await api.post<BackendMessage>(
    `/im/conversations/${threadId}/messages`,
    body,
  );
  return adaptMsg(data, meId);
}

export async function markRead(threadId: string): Promise<void> {
  await api.put(`/im/conversations/${threadId}/read`);
}
